import axios from 'axios'
import clsx from 'clsx'
import {getRandomInt, getTenantDomain} from 'common/HelperFunctions'
import Quill from 'quill'
import {FC, forwardRef, useEffect, useLayoutEffect, useMemo, useRef} from 'react'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import {confirmFileUpload, getSignedUploadUrl} from 'setup/redux/Actions/Site/site'
import store from 'setup/redux/Store'

type Props = {
  label?: string
  labelClassName?: string
  required?: boolean
  title?: string
  acceptImage?: boolean
  value?: string
  placeholder?: string
  onChange?: (value: string) => void
  onKeyDown?: (event: React.KeyboardEvent) => void
  readOnly?: boolean
  className?: string
}

const id = `text-editor-${getRandomInt(0, 100000)}`

const getToolbarOptions = (acceptImage: boolean) => {
  const baseToolbar = [
    [{header: [1, 2, 3, 4, 5, 6, false]}],
    [{font: []}],
    ['bold', 'italic', 'underline', 'strike', 'blockquote'],
    [{color: []}, {background: []}],
    [{list: 'ordered'}, {list: 'bullet'}],
    [{indent: '-1'}, {indent: '+1'}],
    ['link', 'video', 'formula'],
    ['clean'],
  ]
  if (acceptImage) {
    baseToolbar[6].splice(2, 0, 'image')
  }
  return baseToolbar
}

const TextEditorInner = forwardRef<Quill | null, Props>(
  (
    {
      label,
      labelClassName,
      required = false,
      title,
      className,
      acceptImage = true,
      value = '',
      placeholder,
      onChange,
      onKeyDown,
      readOnly = false,
    },
    ref
  ) => {
    const intl = useIntl()
    const containerRef = useRef<HTMLDivElement>(null)
    const onChangeRef = useRef(onChange)
    const valueRef = useRef(value)
    const quillRef = useRef<Quill | null>(null)

    // Update refs on prop changes
    useLayoutEffect(() => {
      onChangeRef.current = onChange
      valueRef.current = value
    }, [onChange, value])

    const labelHasTranslation = label ? !!intl.messages[label] : false
    const titleHasTranslation = title ? !!intl.messages[title] : false

    const renderTooltip = useMemo(() => {
      return (
        <Tooltip id={`tooltip-${id}`}>
          {titleHasTranslation ? intl.formatMessage({id: title}) : title}
        </Tooltip>
      )
    }, [title, intl, titleHasTranslation])

    useEffect(() => {
      if (!containerRef.current) return

      const container = containerRef.current
      const editorContainer = container.appendChild(container.ownerDocument.createElement('div'))

      const quill = new Quill(editorContainer, {
        theme: 'snow',
        readOnly,
        modules: {
          toolbar: {
            container: getToolbarOptions(acceptImage),
            handlers: {
              image: () => {
                const input = document.createElement('input')
                input.setAttribute('type', 'file')
                input.setAttribute('accept', 'image/*')
                input.click()

                input.onchange = async () => {
                  const file = input.files ? input.files[0] : null
                  if (!file) {
                    toast.error('No file selected')
                    return
                  }

                  const domain = getTenantDomain()
                  const tenant = domain.replaceAll('.', '_')
                  const IMAGE_UPLOAD_PATH = `uploads/${tenant}/images`

                  const lastIndex = file.name.lastIndexOf('.')
                  const fileName = file.name.slice(0, lastIndex)
                  const body = {
                    path: IMAGE_UPLOAD_PATH,
                    fileName,
                    contentType: file.type,
                  }

                  const onFailure = (err: any) => {
                    const message =
                      err?.response?.data?.message ??
                      err?.message ??
                      'Error in uploading the message'
                    toast.error(message)
                  }

                  store.dispatch(
                    getSignedUploadUrl(
                      body,
                      (res: any) => {
                        const {fileUrl, uploadURL} = res.result

                        file.arrayBuffer().then((buffer: any) => {
                          toast.info(`Uploading ${file.name}`)
                          axios
                            .put(uploadURL, buffer)
                            .then(() => {
                              const body = {
                                path: IMAGE_UPLOAD_PATH,
                                original_filename: file.name,
                                file_name: fileUrl.split('/').slice(-1).pop(),
                                url: fileUrl,
                                size: file.size,
                                contentType: file.type,
                              }

                              store.dispatch(
                                confirmFileUpload(
                                  body,
                                  () => {
                                    toast.success(`${file.name} uploaded successfully`)
                                    const range = quill.getSelection(true)
                                    quill.insertEmbed(range?.index ?? 0, 'image', fileUrl)
                                    quill.setSelection((range?.index ?? 0) + 1)
                                  },
                                  onFailure
                                )
                              )
                            })
                            .catch(onFailure)
                        })
                      },
                      onFailure
                    )
                  )
                }
              },
            },
          },
        },
        bounds: `#${id}-container`,
        placeholder,
      })

      quillRef.current = quill

      // Set initial content
      if (valueRef.current) {
        quill.clipboard.dangerouslyPasteHTML(valueRef.current)
      }

      quill.on('text-change', () => {
        const html = container.querySelector('.ql-editor')?.innerHTML || ''
        const cleanHtml = html === '<p><br></p>' ? '' : html
        if (onChangeRef.current) onChangeRef.current(cleanHtml)
      })

      if (ref) {
        if (typeof ref === 'function') {
          ref(quill)
        } else {
          ref.current = quill
        }
      }

      return () => {
        if (ref) {
          if (typeof ref === 'function') {
            ref(null)
          } else {
            ref.current = null
          }
        }
        container.innerHTML = ''
        quillRef.current = null
      }
    }, [acceptImage, placeholder, readOnly, ref])

    // Update editor content if value prop changes externally
    useEffect(() => {
      const quill = quillRef.current
      if (!quill) return

      const currentHtml = containerRef.current?.querySelector('.ql-editor')?.innerHTML || ''
      if (value !== currentHtml) {
        const selection = quill.getSelection()
        quill.clipboard.dangerouslyPasteHTML(value)
        if (selection) quill.setSelection(selection)
      }
    }, [value])

    return (
      <>
        {label && (
          <label
            className={clsx('form-label', labelClassName, {
              required,
            })}
            htmlFor={id}
          >
            {labelHasTranslation ? intl.formatMessage({id: label}) : label}
            {title && (
              <OverlayTrigger placement='right' overlay={renderTooltip}>
                <div className='badge pe-auto' style={{cursor: 'pointer'}}>
                  <i className='bi bi-info-circle-fill'></i>
                </div>
              </OverlayTrigger>
            )}
          </label>
        )}
        <div
          id={`${id}-container`}
          className={clsx('border rounded', className)}
          ref={containerRef}
          onKeyDown={onKeyDown}
        />
      </>
    )
  }
)

TextEditorInner.displayName = 'TextEditor'

export const TextEditor = TextEditorInner as FC<Props>
